import { useState } from "react";
import { ArrowRight, RotateCcw, Sparkles, CheckCircle2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QUIZ } from "@/constants/testIds/landing";
import { COURSE_LIST } from "@/data/courses";
import BookingDialog from "@/components/site/BookingDialog";

// Each step has a key and answer options that set a value on the answers object
const STEPS = [
  {
    key: "role",
    question: "Was beschreibt dich am besten?",
    helper: "Wir filtern nur Kurse, die wirklich zu deiner Qualifikation passen.",
    options: [
      { value: "dienst",      label: "Pflegedienst / Einrichtung", sub: "Inhaber:in oder PDL" },
      { value: "fachkraft",   label: "Examinierte Pflegefachkraft", sub: "3-jährige Pflegeausbildung" },
      { value: "helfer",      label: "Pflegehelfer:in / Pflegeassistent:in", sub: "Ohne 3-jährige Ausbildung" },
      { value: "einsteiger",  label: "Quereinsteiger:in", sub: "Neu in der Pflege" },
    ],
  },
  {
    key: "goal",
    question: "Was möchtest du erreichen?",
    helper: "Dein Hauptziel in den nächsten 12 Monaten.",
    options: [
      { value: "einstieg",     label: "In die Pflege einsteigen",         sub: "Erste Qualifikation" },
      { value: "behandlung",   label: "Behandlungspflege LG1/LG2",         sub: "Mehr Verantwortung, mehr Gehalt" },
      { value: "spezialwissen",label: "Spezialwissen aufbauen",            sub: "Wundexperte · Palliativ-Care (nur Fachkraft)" },
      { value: "praxis",       label: "Praxisanleitung",                   sub: "Azubis ausbilden (nur Fachkraft)" },
      { value: "leitung",      label: "In die Leitung (PDL)",              sub: "Pflegedienst leiten (nur Fachkraft)" },
      { value: "pflicht",      label: "Pflichtfortbildungen",              sub: "MD-konform dokumentiert" },
    ],
  },
  {
    key: "start",
    question: "Wann möchtest du starten?",
    helper: "Wir bieten jeden Montag einen Startpunkt.",
    options: [
      { value: "sofort",  label: "Sofort",       sub: "Diese oder nächste Woche" },
      { value: "monate",  label: "In 1–3 Monaten" },
      { value: "spaeter", label: "Später / unverbindlich informieren" },
    ],
  },
];

const FACHKRAFT_ONLY = new Set(["praxis", "leitung", "spezialwissen"]);

// Returns { gated: bool, ids: string[] } – gated means: goal needs Fachkraft, user is not one
function recommendCourses(answers) {
  const { role, goal } = answers;
  const isFachkraft = role === "fachkraft";

  // Hard gate: PDL & Praxisanleitung require Fachkraft – never recommend to non-Fachkräfte
  if (FACHKRAFT_ONLY.has(goal) && !isFachkraft) {
    return {
      gated: true,
      ids: role === "einsteiger"
        ? ["ambulanter-pflegeassistent", "pflegehilfskraft-betreuung"]
        : ["pflegeassistent-lg1-lg2", "ambulanter-pflegeassistent"],
    };
  }

  if (goal === "pflicht") {
    return { gated: false, ids: role === "dienst"
      ? ["paket-gross", "paket-klein"]
      : ["rezert-praxisanleitung", "rezert-betreuungskraft"] };
  }
  if (goal === "leitung")      return { gated: false, ids: ["pdl", "pflegeberater"] };
  if (goal === "praxis")       return { gated: false, ids: ["praxisanleitung", "rezert-praxisanleitung"] };
  if (goal === "spezialwissen") return { gated: false, ids: ["wundexperte", "palliativ-care"] };
  if (goal === "behandlung") {
    return { gated: false, ids: (role === "einsteiger" || role === "helfer")
      ? ["pflegeassistent-lg1-lg2", "ambulanter-pflegeassistent"]
      : ["pflegeassistent-lg1-lg2", "wundexperte"] };
  }
  if (goal === "einstieg") {
    return { gated: false, ids: role === "dienst"
      ? ["ambulanter-pflegeassistent", "pflegehilfskraft-betreuung"]
      : ["ambulanter-pflegeassistent", "betreuungskraft"] };
  }
  return { gated: false, ids: ["ambulanter-pflegeassistent"] };
}

function getCourse(id) {
  return COURSE_LIST.find((c) => c.id === id);
}

export default function CourseQuiz() {
  const [started, setStarted] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});

  const step = STEPS[stepIdx];
  const finished = stepIdx >= STEPS.length;

  function handleSelect(value) {
    const next = { ...answers, [step.key]: value };
    setAnswers(next);
    setStepIdx((i) => i + 1);
  }

  function reset() {
    setStarted(false);
    setStepIdx(0);
    setAnswers({});
  }

  return (
    <section id="quiz" className="relative bg-espresso-deep py-32 md:py-44 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }}
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-brand-rose">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
            In 30 Sekunden zu deiner persönlichen Empfehlung
          </span>
          <h2 className="mt-4 font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-cream-50 leading-[1.08]">
            Finde deinen{" "}
            <span className="italic text-saffron underline decoration-saffron/70 decoration-[5px] underline-offset-[10px]">nächsten Karriereschritt.</span>
          </h2>
          <p className="mt-4 text-cream-100/75 text-base sm:text-lg">
            Beantworte drei kurze Fragen und finde die Weiterbildung, die zu deinem Berufsziel passt.
          </p>
        </div>

        <div className="bg-white border border-white/60 rounded-[2rem] shadow-hover p-6 sm:p-10 lg:p-12 min-h-[420px] flex flex-col">
          {!started && (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="font-magazine font-medium text-[2.4rem] sm:text-[3.2rem] text-sand-800 leading-[1.08] max-w-lg">
                Lass uns den passenden Kurs für dich finden.
              </div>
              <p className="mt-4 text-sand-600 max-w-md">
                100 % kostenlos. Keine Anmeldung. Sofortige Empfehlung.
              </p>
              <Button
                data-testid={QUIZ.start}
                onClick={() => setStarted(true)}
                className="mt-8 rounded-full bg-terra hover:bg-terra-dark text-white h-13 px-8 text-base font-semibold group"
              >
                Jetzt passenden Kurs finden
                <ArrowRight className="ml-2 h-4 w-4 nudge" strokeWidth={1.8} />
              </Button>
            </div>
          )}

          {started && !finished && (
            <div className="flex-1 flex flex-col">
              {/* Progress */}
              <div className="flex items-center gap-2 mb-8">
                {STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-all ${
                      i <= stepIdx ? "bg-terra" : "bg-cream-300"
                    }`}
                  />
                ))}
                <span className="ml-2 text-xs text-sand-500 font-medium tracking-wide">
                  {stepIdx + 1} / {STEPS.length}
                </span>
              </div>

              <div className="max-w-2xl">
                <h3 className="font-magazine text-2xl sm:text-3xl text-sand-800 leading-tight">
                  {step.question}
                </h3>
                <p className="mt-2 text-sm text-sand-600">{step.helper}</p>
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {step.options.map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`${QUIZ.option}-${opt.value}`}
                    onClick={() => handleSelect(opt.value)}
                    className="group text-left bg-cream-50 hover:bg-cream-100 border border-cream-300 hover:border-sand-400 rounded-2xl p-5 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-magazine text-lg text-sand-800 leading-tight">{opt.label}</div>
                        {opt.sub && <div className="mt-1 text-xs text-sand-500">{opt.sub}</div>}
                      </div>
                      <ArrowRight className="h-4 w-4 text-sand-400 group-hover:text-terra group-hover:translate-x-1 transition-all shrink-0 mt-1" strokeWidth={1.6} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {finished && (() => {
            const rec = recommendCourses(answers);
            return (
              <div className="flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-terra">
                  <CheckCircle2 className="h-5 w-5" strokeWidth={1.8} />
                  <span className="text-xs font-semibold tracking-widest uppercase">
                    {rec.gated ? "Wichtiger Hinweis" : "Deine Empfehlung"}
                  </span>
                </div>

                <h3 className="mt-3 font-magazine font-medium text-[2.4rem] sm:text-[3.2rem] text-sand-800 leading-[1.08] max-w-2xl">
                  {rec.gated
                    ? "Dieser Weg braucht zuerst eine Pflegeausbildung."
                    : "Diese Kurse passen am besten zu dir:"}
                </h3>

                {rec.gated && (
                  <div className="mt-5 flex items-start gap-3 rounded-2xl bg-cream-100 border border-cream-300 p-5 max-w-2xl">
                    <Info className="h-5 w-5 text-terra shrink-0 mt-0.5" strokeWidth={1.8} />
                    <p className="text-sand-700 text-[15px] leading-relaxed">
                      {answers.goal === "spezialwissen"
                        ? <>Wundexperte und Palliative Care setzen die <strong>3-jährige Pflegeausbildung</strong> (examinierte Pflegefachkraft) voraus.
                            Wir empfehlen dir den Einstieg über die Kurse unten – damit machst du den ersten Schritt in die Pflege.</>
                        : <>Praxisanleitung und PDL setzen die <strong>3-jährige Pflegeausbildung</strong> (examinierte Pflegefachkraft) voraus.
                            Wir empfehlen dir den Einstieg über die Kurse unten – damit machst du den ersten Schritt in die Pflege.</>}
                    </p>
                  </div>
                )}

                <div className="mt-8 grid md:grid-cols-2 gap-4">
                  {rec.ids.map((id, idx) => {
                    const c = getCourse(id);
                    if (!c) return null;
                    return <ResultCard key={id} course={c} top={idx === 0 && !rec.gated} />;
                  })}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
                  <Button
                    variant="outline"
                    data-testid={QUIZ.restart}
                    onClick={reset}
                    className="rounded-full border-sand-400 text-sand-800 hover:bg-cream-100 bg-transparent h-11 px-5 text-sm"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    Nochmal starten
                  </Button>
                  <a href="#kontakt" className="text-sm text-sand-600 hover:text-sand-800 underline underline-offset-4">
                    Lieber kostenlos beraten lassen?
                  </a>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </section>
  );
}

// Result card – öffnet Buchungsformular vor dem Stripe-Checkout
function ResultCard({ course, top }) {
  const [bookingOpen, setBookingOpen] = useState(false);

  function handleBuy() {
    setBookingOpen(true);
  }

  return (
    <div
      data-testid={QUIZ.result}
      className={`relative rounded-3xl p-7 border ${
        top ? "bg-cream-200 border-terra" : "bg-white border-cream-300"
      }`}
    >
      {top && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-terra text-white text-[10px] font-semibold tracking-widest uppercase rounded-full px-3 py-1">
          Top-Empfehlung
        </span>
      )}
      <div className="font-magazine text-xl leading-tight text-sand-800">{course.title}</div>
      <div className="mt-1 text-sm text-sand-600">{course.subtitle}</div>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-sand-500">
        <span>{course.duration}</span>
        <span>·</span>
        <span>{course.weeks}</span>
        <span>·</span>
        <span className="font-semibold">{course.price}</span>
      </div>
      <Button
        data-testid={QUIZ.resultCta}
        onClick={handleBuy}
        className="mt-5 rounded-full h-11 px-6 text-sm font-semibold bg-terra text-white hover:bg-terra-dark group w-full"
      >
        Jetzt buchen<ArrowRight className="ml-2 h-4 w-4 nudge" strokeWidth={1.8} />
      </Button>
      <BookingDialog course={course} open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>
  );
}
